import React, { useState, useEffect, useCallback } from 'react';
import { heroSlides as defaultSlides } from '../data/heroSlides';
import '../styles/hero-carousel.css';

const HeroCarousel = ({ slides = defaultSlides, intervalMs = 4500 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goTo = useCallback((index) => {
    setActiveIndex((index + slides.length) % slides.length);
  }, [slides.length]);

  const goNext = useCallback(() => {
    goTo(activeIndex + 1);
  }, [activeIndex, goTo]);

  const goPrev = useCallback(() => {
    goTo(activeIndex - 1);
  }, [activeIndex, goTo]);

  useEffect(() => {
    if (isPaused || slides.length <= 1) return undefined;

    const timer = setInterval(goNext, intervalMs);
    return () => clearInterval(timer);
  }, [goNext, intervalMs, isPaused, slides.length]);

  return (
    <div
      className="hero-carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      aria-roledescription="carousel"
      aria-label="Aperçus Nexus Africa Solutions"
    >
      <div className="hero-carousel-frame">
        <div className="hero-carousel-glow" />
        <div className="hero-carousel-track">
          {slides.map((slide, index) => (
            <div
              key={slide.src}
              className={`hero-carousel-slide ${index === activeIndex ? 'active' : ''}`}
              aria-hidden={index !== activeIndex}
            >
              <img
                src={slide.src}
                alt={slide.alt}
                loading={index === 0 ? 'eager' : 'lazy'}
                style={{
                  objectFit: slide.fit || 'cover',
                  objectPosition: slide.position || 'center top'
                }}
              />
              <div className="hero-carousel-overlay" />
              {slide.caption && (
                <div className="hero-carousel-caption">
                  <span className="hero-carousel-caption-dot" />
                  {slide.caption}
                </div>
              )}
            </div>
          ))}
        </div>

        {slides.length > 1 && (
          <>
            <button
              type="button"
              className="hero-carousel-nav hero-carousel-prev"
              onClick={goPrev}
              aria-label="Image précédente"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className="hero-carousel-nav hero-carousel-next"
              onClick={goNext}
              aria-label="Image suivante"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </>
        )}
      </div>

      {slides.length > 1 && (
        <div className="hero-carousel-dots" role="tablist" aria-label="Choisir une image">
          {slides.map((slide, index) => (
            <button
              key={slide.src}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              aria-label={`Image ${index + 1}`}
              className={`hero-carousel-dot ${index === activeIndex ? 'active' : ''}`}
              onClick={() => goTo(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroCarousel;
